import { Inject, Injectable } from '@angular/core';
import { DispatchersFacadeService } from '../state-management/dispatchers-facade.service';
import {
  AnalyticsPageTypeEnum,
  AnalyticsService,
} from 'src/app/shared/analytics/analytics.service';
import { TranslateService } from '@ngx-translate/core';
import { AbstractControl, FormControlStatus, FormGroup } from '@angular/forms';
import { WorkflowSteps } from 'src/app/shared/base/WorkflowSteps';
import { ProcedureInstanceLocalStorageService } from 'src/app/services/es-shared/es-local-storage/procedure-instance-local-storage.service';

@Injectable()
export class AnalyticsListenersService {
  constructor(
    @Inject('dispatcherService')
    private dispatcher: DispatchersFacadeService,
    private analyticsService: AnalyticsService,
    private translateService: TranslateService,
    private procedureInstanceLocalStorageService: ProcedureInstanceLocalStorageService,
  ) {
    this.subscribeToEvents();
  }

  private subscribeToEvents() {
    this.dispatcher.draftRequestLoaded.observable$.subscribe((event) => {
      const requestId = this.emptyForNotANumber(event.procedureInstanceId);
      const formStepName = `${event.serviceKey}-${event.currentStep}`;

      this.analyticsService.loadExistingDraft({
        requestId: requestId,
        formName: event.serviceKey,
        formStepName: formStepName,
      });
    });

    this.dispatcher.draftRequestSaved.observable$.subscribe((event) => {
      const requestId = this.emptyForNotANumber(event.procedureInstanceId);
      const formStepName = `${event.serviceKey}-${event.currentStep}`;

      this.analyticsService.formDraft({
        requestId: requestId,
        formName: event.serviceKey,
        formStepName: formStepName,
      });
    });

    this.dispatcher.serviceRequestUpdated.observable$.subscribe((event) => {
      const requestId = this.emptyForNotANumber(event.procedureInstanceId);

      this.analyticsService.formSubmit({
        requestId: requestId,
        formName: event.serviceKey,
      });
    });

    this.dispatcher.invalidFlow.observable$.subscribe((event) => {
      const formStepName = `${event.serviceKey}-${event.currentStep}`;
      const errors = this.getFormErrorKeys(event.form).join(' | ');

      this.analyticsService.formStepError({
        formName: event.serviceKey,
        errors: errors,
        formStepName: formStepName,
      });
    });

    this.dispatcher.invalidServiceRequestSubmitManager.observable$.subscribe(
      (event) => {
        const errors = this.getFormErrorKeys(event.form).join(' | ');

        this.analyticsService.formSubmitValidateError({
          formName: event.serviceKey,
          errors: errors,
        });
      },
    );

    this.dispatcher.successfulFlow.observable$.subscribe((event) => {
      const formStepName = `${event.serviceKey}-${event.currentStep}`;

      this.analyticsService.serviceStepSuccess({
        formName: event.serviceKey,
        formStepName: formStepName,
      });
    });

    // this.dispatcher.servicePageLanded.observable$.subscribe((event) => {
    //   const formStepName = `${event.serviceKey}-${event.currentStep}`;

    //   this.analyticsService.trackServiceStagePageView({
    //     formName: event.serviceKey,
    //     requestId: event.procedureInstanceId,
    //     serviceName: event.serviceKey,
    //     pageName: event.currentStep,
    //     formStepName: formStepName,
    //     userId: event.registeredUserNumber,
    //   });
    // });

    this.dispatcher.workflowStarted.observable$.subscribe((event) => {
      const formStepName = `${event.serviceKey}-${event.currentStep}`;

      this.analyticsService.trackServiceStagePageView({
        formName: event.serviceKey,
        requestId: event.procedureInstanceId,
        serviceName: event.serviceMetadata?.name || event.serviceKey,
        pageName: event.currentStep,
        formStepName: formStepName,
        userId: event.registeredUserNumber,
      });
    });

    this.dispatcher.formsDefined.observable$.subscribe((event) => {
      for (const form of event.forms) {
        Object.entries(form.controls).forEach(([name, control]) => {
          control.statusChanges.subscribe((status) =>
            this.emitFormControlStatusChange(event, status, control, name),
          );
        });
      }
    });

    this.dispatcher.flow.observable$.subscribe((event) => {
      if (event.currentStep) {
        const pageType = this.resolvePageType(event.currentStep);
        const fullUrl = window.location.origin + event.url;
        const fullPreviousUrl = window.location.origin + event.previousUrl;
        const formStepName = `${event.serviceKey}-${event.currentStep}`;

        this.analyticsService.trackVirtualPageView({
          pageName: event.currentStep,
          customUrl: fullUrl,
          formName: event.serviceKey,
          formStepName: formStepName,
          pageType: pageType,
          referrerUrl: fullPreviousUrl,
          requestId: this.emptyForNotANumber(event.procedureInstanceId),
          serviceName: event.serviceKey,
        });
      }
    });

    this.dispatcher.serviceRequestCreated.observable$.subscribe((event) => {
      const requestId = this.emptyForNotANumber(event.procedureInstanceId);

      this.analyticsService.formSubmit({
        requestId: requestId,
        formName: event.serviceKey,
      });
    });

    this.dispatcher.paymentConfirmed.observable$.subscribe((event) => {
      const instance = this.procedureInstanceLocalStorageService.get(
        event.procedureInstanceId,
      );

      this.analyticsService.paymentSuccess({
        formName: instance.serviceKey,
        requestId: instance.procedureInstanceNumber,
      });
    });

    this.dispatcher.paymentError.observable$.subscribe((event) => {
      const instance = this.procedureInstanceLocalStorageService.get(
        event.procedureInstanceId,
      );

      this.analyticsService.paymentError({
        formName: instance.serviceKey,
        requestId: instance.procedureInstanceNumber,
        errors: event.errors,
      });
    });
  }

  private getFormErrorKeys(form: FormGroup): string[] {
    return Object.values(form.controls).reduce((errors, control) => {
      return errors.concat(Object.keys(control.errors || {}));
    }, []);
  }

  private resolvePageType(currentStep: WorkflowSteps): AnalyticsPageTypeEnum {
    switch (currentStep) {
      case WorkflowSteps.ApplicantConfiguration:
      case WorkflowSteps.ServiceDetailsConfiguration:
      case WorkflowSteps.AttachmentsConfiguration:
      case WorkflowSteps.ReviewConfiguration:
        return AnalyticsPageTypeEnum.ServiceStage;
      case WorkflowSteps.Congratulations:
        return AnalyticsPageTypeEnum.CompletionStage;
    }
  }

  private emptyForNotANumber(requestId: number): string {
    if (requestId) {
      if (Number.isNaN(requestId)) {
        return '';
      }

      return `${requestId}`;
    }

    return '';
  }

  private emitFormControlStatusChange(
    event: {
      serviceKey: string;
      forms: FormGroup[];
    },
    status: FormControlStatus,
    control: AbstractControl,
    name: string,
  ) {
    if (control.invalid && control.touched) {
      const errors = Object.keys(control.errors ?? {});
      this.translateService.getTranslation('en').subscribe((translations) => {
        const errorsTranslations = errors
          .map((errorKey) => translations[errorKey] || errorKey)
          .join(' | ');

        this.analyticsService.formError({
          formName: event.serviceKey,
          errors: errorsTranslations,
          fieldName: name,
        });
      });
    }
  }
}
